import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConnectWithOtp } from "@dynamic-labs/sdk-react-core";

import {
  AnalyticsEvents,
  capturePosthogEvent,
} from "../../common/utils/analytics";
import { RootState } from "../../redux/store";
import { showAlert } from "../../common/utils";
import { Paths } from "../../common/constants";
import { verifyEmail } from "./services/action";
import { getCmsLinks } from "../../common/utils";
import { LoginFormValues } from "./services/types";
import { setLoading } from "../../redux/global.slice";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import { setIsLogin, setUserEmail } from "./services/login.slice";

const useLoginHelper = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    sessionStorage.removeItem("analytics_filters");
    sessionStorage.removeItem("analytics_active_tab");
  }, []);

  const { email: userEmail } = useAppSelector(
    (state: RootState) => state.login,
  );

  const { cmsLinks } = useAppSelector(
    (state: RootState) => state.globalcontent,
  );

  const params = new URLSearchParams(window.location.search);
  const brandId = params.get("brand_id");

  const loginInitialValue = {
    email: userEmail ? userEmail : "",
    password: "",
  };

  const [email, setEmail] = useState<string>("");
  const { connectWithEmail } = useConnectWithOtp();

  const handleSupportClick = () => {
    navigate(Paths.SUPPORT, { state: { email: email } });
  };

  /** on submitting the login form */
  const onSubmitEmailHandler = async (
    values: LoginFormValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void },
  ) => {
    const emailToSubmit = (values?.email || email).trim();
    setSubmitting(true);
    setEmail(emailToSubmit);
    setUserEmail(emailToSubmit);

    try {
      dispatch(setLoading(true));
      dispatch(
        verifyEmail(emailToSubmit, async () => {
          sessionStorage.removeItem("externalRedirectData");
          await connectWithEmail(emailToSubmit)
            .then(() => {
              dispatch(setIsLogin(true));
              dispatch(setLoading(false));
              dispatch(setUserEmail(emailToSubmit));
              if (brandId) localStorage.setItem("brandId", brandId);
              navigate(Paths.OTP, { state: { email: emailToSubmit } });
            })
            .catch((error) => {
              dispatch(setLoading(false));
              const reason =
                error?.code ?? error?.message ?? "connect_with_email_failed";
              capturePosthogEvent(AnalyticsEvents.MANUFACTURER_LOGIN_FAILED, {
                email: emailToSubmit,
                reason,
              });
              if (
                error?.code?.includes("too_many_email_verification_attempts")
              ) {
                showAlert(
                  2,
                  "Too many email verification attempts, please try again later",
                );
              } else if (error?.code?.includes("invalid_email_address")) {
                showAlert(
                  2,
                  "Invalid Email. Email should be in a format as 'Username@domainname.TLD'",
                );
              } else {
                if (error && error?.code) showAlert(2, error?.code);
                else showAlert(2);
              }
              throw new Error("Error connecting with email:", error);
            });
        }),
      );
    } catch (error) {
      dispatch(setLoading(false));
      console.error("Error connecting with email:", error);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (userEmail) dispatch(setUserEmail(""));

    dispatch(setIsLogin(false));
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(getCmsLinks());
    // eslint-disable-next-line
  }, [dispatch]);

  return {
    cmsLinks,
    userEmail,
    loginInitialValue,

    navigate,
    handleSupportClick,
    onSubmitEmailHandler,
  };
};

export default useLoginHelper;
